import { useState, useEffect } from 'react';
import { X, Loader2, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { parcelsApi, type ParcelImportProfile, type ParcelImportSummary } from '../services/api';

/**
 * Slim "run import" modal: pick a saved profile, pick a file, import, show the
 * summary. Profiles (parse options + column mapping) are created and managed in
 * the dedicated "Profils d'import" tab.
 */
export default function ParcelImportModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const { t } = useTranslation();

  const [profiles, setProfiles] = useState<ParcelImportProfile[]>([]);
  const [profileId, setProfileId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ParcelImportSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    parcelsApi
      .listImportProfiles()
      .then(({ profiles: list, activeProfileId }) => {
        setProfiles(list);
        setProfileId(activeProfileId ?? list[0]?.id ?? '');
      })
      .catch(() => { /* no profiles yet */ });
  }, []);

  const handleImport = async () => {
    if (!file || !profileId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await parcelsApi.runImport(file, profileId);
      setSummary(res);
      onImported();
    } catch (e) {
      setError((e as { response?: { data?: { error?: string } } }).response?.data?.error || t('parcels.import.err'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-primary-600" /> {t('parcels.import.title')}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          {profiles.length === 0 ? (
            <div className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {t('parcels.import.no_profile_hint')}
            </div>
          ) : (
            <label className="block">
              <span className="block text-xs font-medium text-gray-600 mb-1">{t('parcels.import.profile')}</span>
              <select
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
                className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.isActive ? ' ★' : ''}</option>
                ))}
              </select>
              <span className="block text-[11px] text-gray-400 mt-1">{t('parcels.import.manage_hint')}</span>
            </label>
          )}

          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">{t('parcels.import.file')}</span>
            <label
              className={`flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer text-sm transition-colors ${
                file ? 'border-primary-300 bg-primary-50/40 text-gray-800' : 'border-gray-300 hover:border-primary-400 hover:bg-primary-50/30 text-gray-600'
              }`}
            >
              <Upload className="w-5 h-5 text-primary-600 shrink-0" />
              <span className="truncate">{file ? file.name : t('parcels.import.choose_file')}</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xls,.ods,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => { setFile(e.target.files?.[0] ?? null); setSummary(null); }}
              />
            </label>
          </div>

          {summary && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-green-800">
                <CheckCircle2 className="w-4 h-4" /> {t('parcels.import.done')}
              </div>
              <ul className="mt-1 text-green-900 grid grid-cols-2 gap-x-4">
                <li>{t('parcels.import.created')}: {summary.created}</li>
                <li>{t('parcels.import.updated')}: {summary.updated}</li>
                <li>{t('parcels.import.skipped')}: {summary.skippedInvalid}</li>
                <li>{t('parcels.import.total')}: {summary.totalRows}</li>
              </ul>
              {summary.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-amber-700">{t('parcels.import.errors', { n: summary.errors.length })}</summary>
                  <ul className="mt-1 text-xs text-gray-600 max-h-32 overflow-y-auto">
                    {summary.errors.slice(0, 50).map((er, i) => (
                      <li key={i}>{t('parcels.import.row')} {er.row}: {er.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">{t('parcels.import.close')}</button>
          <button
            onClick={handleImport}
            disabled={busy || !file || !profileId}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {t('parcels.import.run')}
          </button>
        </div>
      </div>
    </div>
  );
}
